// src/pages/useReadingPrefs.js
//
// Reading-prefs hook (R2): NOT React Context -- a plain hook backed by localStorage key
// `btl.readingPrefs`. The shared reader shell owns one instance and passes the derived
// classes downward. Survives reload (R5). Pure normalization/derivation lives in
// readingPrefs.js (unit-tested).
import { useState, useCallback, createContext } from 'react';
import { DEFAULT_PREFS, READING_PREFS_KEY, normalizePrefs } from './readingPrefs.js';

// Read-only context so reader descendants (notably a PORTALED AI drawer, which leaves
// the shell's DOM subtree but stays in the React tree) can read the live colorMode/
// readingScale and re-apply the skin classes. ReaderPageShell owns the single
// useReadingPrefs instance and provides it; portals preserve React context, so the
// drawer stays in sync when the prefs bar toggles.
export const ReadingPrefsContext = createContext(DEFAULT_PREFS);

function loadPrefs() {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(READING_PREFS_KEY) : null;
        return raw ? normalizePrefs(JSON.parse(raw)) : DEFAULT_PREFS;
    } catch {
        return DEFAULT_PREFS;
    }
}

function persist(prefs) {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(READING_PREFS_KEY, JSON.stringify(prefs));
    } catch {
        /* ignore quota / privacy-mode errors -- prefs still apply for this session */
    }
}

export function useReadingPrefs() {
    const [prefs, setPrefs] = useState(loadPrefs); // lazy: read storage once on mount

    const update = useCallback((patch) => {
        setPrefs((p) => {
            const next = normalizePrefs({ ...p, ...patch });
            persist(next);
            return next;
        });
    }, []);

    const setColorMode = useCallback((colorMode) => update({ colorMode }), [update]);
    const setReadingScale = useCallback((readingScale) => update({ readingScale }), [update]);

    return { colorMode: prefs.colorMode, readingScale: prefs.readingScale, setColorMode, setReadingScale };
}

export default useReadingPrefs;
